/**
 * CHRONO SESSION - SIGN IN PAGE
 * 
 * Professional compact layout with OTP verification
 * Fetches user profile from Supabase 'users' table (not auth metadata)
 */

import { useEffect, useState, useRef } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { supabase, type User } from '../lib/supabaseClient'
import { Eye, EyeOff, CheckCircle, AlertCircle, ArrowRight, Mail, ArrowLeft, Home } from 'lucide-react'

type AuthStep = 'form' | 'otp' | 'success'

// Quick sync check if user is logged in (from localStorage)
function hasStoredSession(): boolean {
  try {
    // Check for Supabase auth token in localStorage
    const keys = Object.keys(localStorage);
    for (const key of keys) {
      if (key.startsWith('sb-') && key.includes('auth-token')) {
        const data = localStorage.getItem(key);
        if (data) {
          const parsed = JSON.parse(data);
          // Check if token exists and not expired
          if (parsed?.access_token && parsed?.expires_at) {
            const expiresAt = parsed.expires_at * 1000; // Convert to ms
            if (Date.now() < expiresAt) {
              return true;
            }
          }
        }
      }
    }
  } catch (e) {
    // Ignore parse errors
  }
  return false;
}

// Get cached profile from localStorage (instant load)
function getCachedProfile(): { name: string; avatar_url: string; email: string } | null {
  try {
    const cached = localStorage.getItem('chrono_profile_cache');
    if (cached) {
      return JSON.parse(cached);
    }
  } catch (e) {}
  return null;
}

// Save profile to localStorage
function saveCachedProfile(name: string, avatar_url: string, email: string) {
  try {
    localStorage.setItem('chrono_profile_cache', JSON.stringify({ name, avatar_url, email }));
  } catch (e) {}
}

// Clear cached profile (on sign out)
function clearCachedProfile() {
  try {
    localStorage.removeItem('chrono_profile_cache');
    localStorage.removeItem('chrono_extension_id');
  } catch (e) {}
}

export default function SignInPage() {
  // Check for cached profile (instant load) - use state so it can be updated
  const [cachedProfile, setCachedProfile] = useState<{name: string; avatar_url: string; email: string} | null>(getCachedProfile());
  const [hasSession, setHasSession] = useState(hasStoredSession());
  
  const [user, setUser] = useState<User | null>(null)
  // If we have cached profile, don't show loading - show cached data instantly
  const [loading, setLoading] = useState(hasSession && !cachedProfile)
  const [isSignUp, setIsSignUp] = useState(false)
  const [searchParams] = useSearchParams()
  const isExtension = searchParams.get('source') === 'extension'

  // Initialize with cached values if available
  const [displayName, setDisplayName] = useState(cachedProfile?.name || '')
  const [avatarUrl, setAvatarUrl] = useState(cachedProfile?.avatar_url || '')
  const [userEmail, setUserEmail] = useState(cachedProfile?.email || '')
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  // Forgot Password Flow (INLINE)
  const [showForgotPassword, setShowForgotPassword] = useState(false)
  const [resetEmail, setResetEmail] = useState('')
  const [resetSent, setResetSent] = useState(false)
  
  // Password Reset Mode (from email link)
  const [isResettingPassword, setIsResettingPassword] = useState(false)
  const isResettingPasswordRef = useRef(false)  // Ref for closure-safe access
  const [newPassword, setNewPassword] = useState('')
  const [confirmNewPassword, setConfirmNewPassword] = useState('')
  const [showNewPassword, setShowNewPassword] = useState(false)
  
  // OTP Flow
  const [authStep, setAuthStep] = useState<AuthStep>('form')
  const [otp, setOtp] = useState('')
  
  // Track if profile has been fetched to avoid duplicate calls
  const profileFetchedRef = useRef<string | null>(null)
  
  // CRITICAL: Set up PASSWORD_RECOVERY listener FIRST before any other auth logic
  // This MUST run before any other auth processing to catch the recovery event
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event) => {
      if (event === 'PASSWORD_RECOVERY') {
        isResettingPasswordRef.current = true  // Set ref FIRST for immediate access
        setIsResettingPassword(true)
        setLoading(false)
      }
    })
    
    return () => {
      subscription.unsubscribe()
    }
  }, [])
  
  // Also check URL for recovery token (backup detection)
  useEffect(() => {
    const hashParams = new URLSearchParams(window.location.hash.substring(1))
    const type = hashParams.get('type')
    const errorCode = hashParams.get('error_code')
    const errorDesc = hashParams.get('error_description')
    
    // If type=recovery in URL, set recovery mode
    if (type === 'recovery') {
      isResettingPasswordRef.current = true  // Set ref FIRST
      setIsResettingPassword(true)
      setLoading(false)
    }
    
    // Handle errors
    if (errorCode === 'otp_expired') {
      setError('Reset link has expired. Please request a new one.')
    } else if (errorDesc) {
      setError(decodeURIComponent(errorDesc))
    }
  }, [])
  
  const passwordsMatch = !isSignUp || !confirmPassword || password === confirmPassword

  // Track if profile has been loaded
  const [, setProfileLoaded] = useState(!!cachedProfile);

  // Fetch profile from Supabase 'users' table and cache it
  const fetchAndSetProfile = async (userId: string, userEmailAddr: string): Promise<boolean> => {
    // Prevent duplicate fetches for same user
    if (profileFetchedRef.current === userId) {
      setProfileLoaded(true);
      setLoading(false);
      return true;
    }
    profileFetchedRef.current = userId;

    try {
      const { data, error } = await supabase
        .from('users')
        .select('name, avatar_url')
        .eq('id', userId)
        .single();
      
      if (error) {
        setProfileLoaded(true);
        setLoading(false);
        return false;
      }
      
      if (data) {
        console.log('✅ Profile data from DB:', data);
        const profileName = data.name || '';
        const profileAvatar = data.avatar_url || '';
        
        setDisplayName(profileName);
        setAvatarUrl(profileAvatar);
        setUserEmail(userEmailAddr);
        
        // Cache to localStorage for instant load next time
        saveCachedProfile(profileName, profileAvatar, userEmailAddr);
        // Update React state
        setCachedProfile({ name: profileName, avatar_url: profileAvatar, email: userEmailAddr });
      }
      setProfileLoaded(true);
      setLoading(false);
      return true;
    } catch (err) {
      console.error('❌ Failed to fetch profile:', err);
      setProfileLoaded(true);
      setLoading(false);
      return false;
    }
  };

  // Notify extension about auth
  const notifyExtension = (user: User, token: string) => {
    window.postMessage({
      type: 'CHRONO_AUTH_SUCCESS', 
      payload: { 
        user: { 
          id: user.id, 
          email: user.email, 
          name: displayName || user.email?.split('@')[0],
          avatar_url: avatarUrl
        }, 
        token, 
        provider: 'supabase' 
      }
    }, '*')
  }

  // Main auth effect - runs once on mount
  useEffect(() => {
    let isMounted = true;

    const initAuth = async () => {
      try {
        // CRITICAL: Check if we're in recovery mode FIRST to avoid lock conflicts
        const hashParams = new URLSearchParams(window.location.hash.substring(1))
        const isRecoveryMode = hashParams.get('type') === 'recovery' && hashParams.get('access_token')
        
        if (isRecoveryMode) {
          // In recovery mode - skip auth init completely to avoid lock contention
          isResettingPasswordRef.current = true
          setIsResettingPassword(true)
          setLoading(false)
          return
        }

        // Handle OAuth callback with access_token in hash
        if (window.location.hash?.includes('access_token')) {
          const hashParams = new URLSearchParams(window.location.hash.substring(1))
          const access_token = hashParams.get('access_token')
          const refresh_token = hashParams.get('refresh_token')
          
          if (access_token) {
            const { data, error } = await supabase.auth.setSession({ 
              access_token, 
              refresh_token: refresh_token || '' 
            })
            
            if (!error && data.session && isMounted) {
              setUser(data.session.user)
              setUserEmail(data.session.user.email || '')
              await fetchAndSetProfile(data.session.user.id, data.session.user.email || '')
              return
            }
          }
        }

        // Get current session
        const { data: { session } } = await supabase.auth.getSession()
        
        if (session?.user && isMounted) {
          setUser(session.user)
          setUserEmail(session.user.email || '')
          await fetchAndSetProfile(session.user.id, session.user.email || '')
          // fetchAndSetProfile handles setLoading(false)
        } else if (isMounted) {
          setLoading(false)
        }
      } catch (err) {
        console.error('Auth init error:', err)
        if (isMounted) setLoading(false)
      }
    }

    initAuth()

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      // CRITICAL: Handle PASSWORD_RECOVERY event FIRST - show password form instead of auto sign-in
      if (event === 'PASSWORD_RECOVERY') {
        isResettingPasswordRef.current = true
        setIsResettingPassword(true)
        setLoading(false)
        return  // Don't auto sign-in, wait for user to set new password
      }
      
      // If we're in password reset mode, don't process sign-in events (use ref for closure-safe check)
      if (isResettingPasswordRef.current) {
        setLoading(false)
        return
      }
      
      if (session?.user && isMounted) {
        setUser(session.user)
        setUserEmail(session.user.email || '')
        setError(null)
        
        // Only fetch if not already fetched for this user
        if (profileFetchedRef.current !== session.user.id) {
          setProfileLoaded(false)
          await fetchAndSetProfile(session.user.id, session.user.email || '')
        } else {
          // Already fetched, just ensure loading is off
          setLoading(false)
        }
      } else if (isMounted) {
        setUser(null)
        setDisplayName('')
        setAvatarUrl('')
        setUserEmail('')
        profileFetchedRef.current = null
        setProfileLoaded(false)
        setLoading(false)
        // Clear cache on logout
        clearCachedProfile()
      }
    })

    return () => {
      isMounted = false
      subscription.unsubscribe()
    }
  }, [])

  // Notify extension when user and profile are ready
  useEffect(() => {
    const notifyIfReady = async () => {
      if (isExtension && user && displayName) {
        const { data: { session } } = await supabase.auth.getSession()
        if (session?.access_token) {
          notifyExtension(user, session.access_token)
        }
      }
    }
    notifyIfReady()
  }, [isExtension, user, displayName])



  const handleGoogleLogin = async () => {
    try {
      setLoading(true)
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: { 
          redirectTo: window.location.origin + '/auth/callback', 
          queryParams: { access_type: 'offline', prompt: 'consent' } 
        },
      })
      if (error) throw error
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed')
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    if (isSignUp && !passwordsMatch) { setError("Passwords do not match"); return }
    setLoading(true)
    try {
      if (isSignUp) {
        const { data, error } = await supabase.auth.signUp({ 
          email, 
          password, 
          options: { 
            data: { full_name: name }
          } 
        })
        if (error) throw error
        if (data.user?.identities?.length === 0) {
          setError('Account exists. Please sign in.')
          setIsSignUp(false)
        } else if (data.user) {
          // Move to OTP step
          setAuthStep('otp')
        }
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password })
        if (error) throw error
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Authentication failed'
      if (msg.includes('already registered')) { setError('Email already registered. Sign in instead.'); setIsSignUp(false) }
      else setError(msg)
    } finally { setLoading(false) }
  }

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)

    try {
      // SECURITY: Let Supabase handle user validation server-side
      // Never reveal if email exists (prevents enumeration attacks)
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: `${window.location.origin}/signin`,
      })

      if (error) throw error

      // Always show success (security best practice)
      setResetSent(true)
    } catch (err) {
      console.error('Reset error:', err)
      setError(err instanceof Error ? err.message : 'Failed to send reset email')
    } finally {
      setLoading(false)
    }
  }

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (newPassword !== confirmNewPassword) {
      setError('Passwords do not match')
      return
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters')
      return
    }

    setLoading(true)

    try {
      // Supabase automatically establishes recovery session from URL token
      // We just need to update the password
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      })

      if (error) throw error

      // Success - clear hash and sign out
      window.location.hash = ''
      
      // Sign out the recovery session
      await supabase.auth.signOut()
      
      // Show success and redirect
      alert('✅ Password updated successfully! Please sign in with your new password.')
      
      setTimeout(() => {
        window.location.href = '/signin'
      }, 100)
    } catch (err) {
      console.error('Password update error:', err)
      const errorMessage = err instanceof Error ? err.message : 'Failed to update password'
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      const { data, error } = await supabase.auth.verifyOtp({
        email,
        token: otp,
        type: 'signup'
      })
      if (error) throw error
      if (data.session) {
        setUser(data.session.user)
        setUserEmail(data.session.user.email || '')
        setAuthStep('success')
        // Fetch profile from DB for this user
        await fetchAndSetProfile(data.session.user.id, data.session.user.email || '')
        if (isExtension) notifyExtension(data.session.user, data.session.access_token)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid code. Please try again.')
    } finally { setLoading(false) }
  }

  const handleResendOtp = async () => {
    setError(null)
    setLoading(true)
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email
      })
      if (error) throw error
      setError(null)
      alert('New code sent to your email!')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resend code')
    } finally { setLoading(false) }
  }

  const handleSignOut = async () => {
    try {
      setLoading(true)
      
      // Clear cached profile FIRST (before signOut)
      clearCachedProfile()
      setCachedProfile(null)
      setHasSession(false)
      
      // Sign out from Supabase
      await supabase.auth.signOut({ scope: 'global' })
      
      // Clear all user-related states
      setUser(null)
      setDisplayName('')
      setAvatarUrl('')
      setUserEmail('')
      setAuthStep('form')
      setError(null)
      
      // Notify extension to clear auth (if content script is listening)
      window.postMessage({ type: 'CHRONO_SIGN_OUT', payload: {} }, '*')
      
      // Clear localStorage items related to auth
      localStorage.removeItem('chrono_profile_cache')
      localStorage.removeItem('sb-eihlnkgjwxqbyvqbgcdx-auth-token')
      
      // Force immediate page reload - use replace to prevent back button issues
      window.location.replace('/signin')
    } catch (err) {
      // Even on error, try to redirect
      window.location.replace('/signin')
    }
  }

  // Loading
  if (loading) {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-gray-100 gap-4">
        <div className="animate-spin rounded-full h-10 w-10 border-3 border-indigo-600 border-t-transparent"></div>
        <p className="text-gray-500 text-sm animate-pulse">Please wait a moment...</p>
      </div>
    )
  }

  // OTP Verification Step
  if (authStep === 'otp') {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-100 p-4">
        <div className="bg-white p-6 rounded shadow-lg border max-w-sm w-full">
          <button onClick={() => setAuthStep('form')} className="text-gray-400 hover:text-gray-600 mb-4 flex items-center gap-1 text-sm">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div className="text-center mb-6">
            <Mail className="w-10 h-10 text-indigo-600 mx-auto mb-3" />
            <h2 className="text-lg font-bold text-gray-900">Verify Your Email</h2>
            <p className="text-gray-500 text-sm mt-1">Enter the 6-digit code sent to <strong>{email}</strong></p>
          </div>
          
          {error && (
            <div className="bg-red-50 text-red-600 p-2.5 rounded text-xs mb-4 border border-red-200 flex items-start gap-2">
              <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" /><span>{error}</span>
            </div>
          )}

          <form onSubmit={handleVerifyOtp} className="space-y-4">
            <input 
              type="text" 
              required 
              value={otp} 
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              maxLength={6}
              className="w-full px-4 py-3 border rounded text-center text-2xl tracking-[0.5em] font-mono focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
            />
            <button type="submit" disabled={otp.length !== 6} className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white font-medium py-2.5 rounded text-sm">
              Verify Email
            </button>
          </form>
          
          <div className="mt-4 text-center">
            <button onClick={handleResendOtp} className="text-sm text-indigo-600 hover:underline">
              Didn't receive code? Resend
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Success Step - Show if user is logged in OR we have cached profile with valid session
  const showSuccess = user || (cachedProfile && hasSession);
  
  if (authStep === 'success' || showSuccess) {
    // Use cached data if user object not loaded yet
    const showName = displayName || cachedProfile?.name || user?.email?.split('@')[0] || 'User';
    const showAvatar = avatarUrl || cachedProfile?.avatar_url || '';
    const showEmail = userEmail || cachedProfile?.email || user?.email || '';
    const initials = showName.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2);
    
    return (
      <div className="h-screen flex items-center justify-center bg-gray-100 p-4">
        <div className="bg-white p-6 rounded shadow-lg border max-w-xs w-full text-center">
          {/* Avatar or Initials */}
          <div className="w-16 h-16 rounded-full mx-auto mb-3 overflow-hidden bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center ring-4 ring-green-100">
            {showAvatar ? (
              <img src={showAvatar} alt={showName} className="w-full h-full object-cover" />
            ) : (
              <span className="text-white font-semibold text-xl">{initials}</span>
            )}
          </div>
          <h2 className="text-lg font-bold text-gray-900">Welcome, {showName}!</h2>
          <p className="text-gray-500 mb-6 text-sm">{showEmail}</p>
          
          {isExtension && (
            <div className="bg-green-50 text-green-700 p-3 rounded text-xs flex items-center gap-2 border border-green-200 mb-4 text-left">
              <CheckCircle className="w-4 h-4 flex-shrink-0" />
              <span>Extension connected. You can close this tab.</span>
            </div>
          )}

          <div className="space-y-3">
            <button 
              onClick={() => {
                // Always use postMessage - the content script will open dashboard via background script
                // This avoids Chrome's popup blocker for chrome-extension:// URLs
                window.postMessage({ type: 'CHRONO_OPEN_DASHBOARD', payload: {} }, '*');
                
                // Give content script 800ms to respond, then fallback to web app
                setTimeout(() => {
                  const extensionId = localStorage.getItem('chrono_extension_id');
                  if (!extensionId) {
                    // No extension detected, go to web app
                    window.location.href = '/app';
                  }
                  // If extension ID exists, content script should have opened dashboard
                }, 800);
              }} 
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-2 rounded text-sm flex items-center justify-center gap-2"
            >
              Open Dashboard <ArrowRight className="w-4 h-4" />
            </button>
            
            <button 
              onClick={handleSignOut} 
              className="w-full py-2 rounded border border-gray-300 text-gray-600 text-sm hover:bg-gray-50"
            >
              Sign Out
            </button>
          </div>
        </div>
      </div>
    )
  }

  // AUTH FORM - COMPACT LAYOUT
  return (
    <div className="h-screen flex items-center justify-center bg-gray-100 p-4">
      <div className="w-full max-w-sm bg-white rounded shadow-lg border">
        
        {/* Header - Compact */}
        <div className="px-6 pt-5 pb-4 text-center border-b">
          <img src="/og-image.png" alt="Chrono Session" className="h-10 mx-auto mb-2 object-contain" />
          <p className="text-gray-500 text-xs">Sync your browser sessions</p>
        </div>

        {/* Tabs */}
        <div className="flex border-b text-sm">
          <button onClick={() => { setIsSignUp(false); setError(null) }} className={`flex-1 py-2.5 font-medium ${!isSignUp ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-gray-400 hover:text-gray-600'}`}>Sign In</button>
          <button onClick={() => { setIsSignUp(true); setError(null) }} className={`flex-1 py-2.5 font-medium ${isSignUp ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-gray-400 hover:text-gray-600'}`}>Sign Up</button>
        </div>

        <div className="p-5">
          {/* Google */}
          <button onClick={handleGoogleLogin} className="w-full flex items-center justify-center gap-2 border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium py-2 rounded mb-4">
            <img src="https://www.google.com/favicon.ico" alt="" className="w-4 h-4" /> Continue with Google
          </button>

          <div className="relative mb-4">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t"></div></div>
            <div className="relative flex justify-center"><span className="bg-white px-2 text-xs text-gray-400">OR</span></div>
          </div>

          {error && (
            <div className="bg-red-50 text-red-600 p-2.5 rounded text-xs mb-4 border border-red-200 flex items-start gap-2">
              <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" /><span>{error}</span>
            </div>
          )}

          {/* PASSWORD RESET FORM (from email link) */}
          {isResettingPassword ? (
            <form onSubmit={handleUpdatePassword} className="space-y-3">
              <div className="bg-blue-50 p-3 rounded text-sm text-blue-800 border border-blue-200 mb-3">
                Create your new password
              </div>

              <div className="relative">
                <input
                  type={showNewPassword ? "text" : "password"}
                  required
                  minLength={6}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="New Password"
                  className="w-full px-3 py-2 border rounded text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none pr-9"
                />
                <button type="button" onClick={() => setShowNewPassword(!showNewPassword)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>

              <input
                type={showNewPassword ? "text" : "password"}
                required
                minLength={6}
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                placeholder="Confirm New Password"
                className="w-full px-3 py-2 border rounded text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
              />

              <button
                type="submit"
                disabled={loading || newPassword !== confirmNewPassword}
                className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white font-medium py-2 rounded text-sm"
              >
                {loading ? 'Updating...' : 'Update Password'}
              </button>
            </form>
          ) : showForgotPassword && !resetSent ? (
            <form onSubmit={handleForgotPassword} className="space-y-3">
              <button 
                onClick={() => { setShowForgotPassword(false); setError(null); setResetEmail('') }} 
                className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-indigo-600 mb-2"
                type="button"
              >
                <ArrowLeft className="w-3.5 h-3.5" /> Back to Sign In
              </button>

              <div className="bg-blue-50 p-3 rounded text-sm text-blue-800 border border-blue-200">
                Enter your email to receive a password reset link
              </div>

              <input 
                type="email" 
                required 
                value={resetEmail} 
                onChange={(e) => setResetEmail(e.target.value)} 
                placeholder="Email address" 
                className="w-full px-3 py-2 border rounded text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none" 
              />

              <button 
                type="submit" 
                disabled={loading}
                className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white font-medium py-2 rounded text-sm"
              >
                {loading ? 'Sending...' : 'Send Reset Link'}
              </button>
            </form>
          ) : showForgotPassword && resetSent ? (
            /* SUCCESS STATE */
            <div className="text-center py-4">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Check Your Email</h3>
              <p className="text-sm text-gray-600 mb-4">
                We've sent a password reset link to <strong>{resetEmail}</strong>
              </p>
              <button 
                onClick={() => { setShowForgotPassword(false); setResetSent(false); setResetEmail(''); setError(null) }}
                className="text-sm text-indigo-600 hover:underline"
              >
                Back to Sign In
              </button>
            </div>
          ) : (
            /* NORMAL SIGN IN/UP FORM */
          <form onSubmit={handleSubmit} className="space-y-3">
            {isSignUp && (
              <input type="text" required value={name} onChange={(e) => setName(e.target.value)} placeholder="Full Name" className="w-full px-3 py-2 border rounded text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none" />
            )}
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" autoComplete="email" className="w-full px-3 py-2 border rounded text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none" />
            <div className="relative">
              <input type={showPassword ? "text" : "password"} required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" autoComplete={isSignUp ? "new-password" : "current-password"} className="w-full px-3 py-2 border rounded text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none pr-9" />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {isSignUp && (
              <>
                <input type="password" required value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Confirm Password" autoComplete="new-password" className={`w-full px-3 py-2 border rounded text-sm focus:ring-2 focus:ring-indigo-500 outline-none ${!passwordsMatch ? 'border-red-400' : ''}`} />
                {!passwordsMatch && <p className="text-xs text-red-500 -mt-1">Passwords do not match</p>}
              </>
            )}
            
            {/* Forgot Password Button - INLINE */}
            {!isSignUp && (
              <div className="flex justify-end">
                <button 
                  type="button"
                  onClick={() => { setShowForgotPassword(true); setError(null) }} 
                  className="text-xs text-indigo-600 hover:text-indigo-700 hover:underline"
                >
                  Forgot password?
                </button>
              </div>
            )}
            
            <button type="submit" disabled={isSignUp && !passwordsMatch} className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white font-medium py-2 rounded text-sm flex items-center justify-center gap-1.5">
              {isSignUp ? 'Create Account' : 'Sign In'} {!isSignUp && <ArrowRight className="w-3.5 h-3.5" />}
            </button>
          </form>
          )}
          
          {/* Go to Home Link */}
          <div className="mt-4 pt-4 border-t border-gray-100 text-center">
            <Link 
              to="/" 
              className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-indigo-600 transition-colors"
            >
              <Home className="w-3.5 h-3.5" />
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
